<template>
  <transition name="modal">
    <div
      class="modal-mask"
      :style="`z-index:${calculateMaskZindex}`"
      @mousedown.stop="preventClose ? '' : $emit('close')"
    >
      <div class="modal-wrapper">
        <div
          class="modal-container"
          @mousedown.stop
          :style="{
            maxWidth: width,
            maxHeight: height,
            width: '100%',
            height: height,
            padding: padding,
          }"
        >
          <div
            v-if="header"
            class="flex justify-between font-bold bg-white"
          >
            <div class="modal-header-content">
              <slot name="header"> </slot>
            </div>
            <div class="modal-header-close">
              <img
                @click="$emit('close')"
                src="@/assets/image/icon/mobile-close.webp"
                class="w-5 h-5 cursor-pointer"
              />
            </div>
          </div>

          <div class="h-full overflow-y-auto">
            <slot name="body"> </slot>
          </div>

          <div class="modal-footer" v-if="footer">
            <slot name="footer"> </slot>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, toRefs, onMounted } from "vue";
import useHoldScroll from "@/lib/useHoldScroll";

export default defineComponent({
  name: "BaseModalComponent",
  props: {
    width: { type: String, default: "auto" },
    height: { type: String, default: "auto" },
    header: { type: Boolean, default: true },
    footer: { type: Boolean, default: true },
    preventClose: { type: Boolean, default: false },
    zindex: { type: Number as PropType<1 | 2 | 3>, default: 1 },
    padding: { type: String, default: "1.5em" },
  },
  setup: (props, { emit, slots }) => {
    const { zindex } = toRefs(props);
    const { fetchHoldAction } = useHoldScroll();
    const calculateMaskZindex = computed(() => {
      switch (zindex.value) {
        case 1:
          return 9997;
        case 2:
          return 9998;
        case 3:
          return 9999;
        default:
          return 9997;
      }
    });

    onMounted(() => {
      window.addEventListener("keyup", function (e: KeyboardEvent) {
        if (e.key?.toLowerCase() === "escape") {
          emit("close");
        }
      });
    });

    fetchHoldAction();
    return {
      calculateMaskZindex,
      props,
    };
  },
});
</script>

<style lang="scss" scoped>
.modal-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: table;
  transition: opacity 0.3s ease;
  padding-left: 20px;
  padding-right: 20px;
}
.modal-wrapper {
  display: table-cell;
  vertical-align: middle;
}
.modal-container {
  min-width: 320px;
  margin: 0px auto;
  padding: 1.5em;
  background-color: var(--white);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
/*
 * The following styles are auto-applied to elements with
 * transition="modal" when their visibility is toggled
 * by Vue.js.
 *
 * You can easily play with the modal transition by editing
 * these styles.
 */
.modal-enter {
  opacity: 0;
}
.modal-leave-active {
  opacity: 0;
}
.modal-enter .modal-container,
.modal-leave-active .modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}
.modal-header-content {
  font-size: 16px;
}
</style>
